import { supabase } from '../../lib/supabase';
import type { LeaseApplication, ApplicationType, ApplicationStatus, ApplicationDocument } from '../../types/application';

export const supabaseApplicationService = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('lease_applications')
      .select(`
        *,
        lot:land_lots!inner (
          lot_id,
          lot_community,
          lot_perm_use
        ),
        applicant:leaseholders!inner (
          holder_id,
          holder_name,
          holder_contact,
          holder_email,
          holder_phone
        ),
        documents:application_documents (
          document_id,
          document_type,
          document_url,
          uploaded_by,
          uploaded_at
        ),
        status_history:application_status_history (
          history_id,
          previous_status,
          new_status,
          changed_by,
          changed_at,
          notes
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }

    return data;
  },

  getById: async (id: string) => {
    const { data, error } = await supabase
      .from('lease_applications')
      .select(`
        *,
        lot:land_lots!inner (
          lot_id,
          lot_community,
          lot_perm_use
        ),
        applicant:leaseholders!inner (
          holder_id,
          holder_name,
          holder_contact,
          holder_email,
          holder_phone
        ),
        documents:application_documents (
          document_id,
          document_type,
          document_url,
          uploaded_by,
          uploaded_at
        ),
        status_history:application_status_history (
          history_id,
          previous_status,
          new_status,
          changed_by,
          changed_at,
          notes
        )
      `)
      .eq('application_id', id)
      .single();

    if (error) throw error;
    return data;
  },

  create: async (application: Omit<LeaseApplication, 'application_id' | 'created_at' | 'updated_at'>) => {
    const { data, error } = await supabase
      .from('lease_applications')
      .insert([application])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  update: async (id: string, updates: Partial<LeaseApplication>) => {
    const { data, error } = await supabase
      .from('lease_applications')
      .update(updates)
      .eq('application_id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  updateStatus: async (id: string, newStatus: ApplicationStatus, notes?: string) => {
    const { data, error } = await supabase
      .from('lease_applications')
      .update({ 
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('application_id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  addDocument: async (document: Omit<ApplicationDocument, 'document_id' | 'uploaded_at'>) => {
    const { data, error } = await supabase
      .from('application_documents')
      .insert([document])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  deleteDocument: async (documentId: string) => {
    const { error } = await supabase
      .from('application_documents')
      .delete()
      .eq('document_id', documentId);

    if (error) throw error;
  },

  getByFilters: async (filters: {
    type?: ApplicationType;
    status?: ApplicationStatus;
    lot_id?: string;
    applicant_id?: number;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    let query = supabase
      .from('lease_applications')
      .select(`
        *,
        lot:land_lots!inner (
          lot_id,
          lot_community,
          lot_perm_use
        ),
        applicant:leaseholders!inner (
          holder_id,
          holder_name,
          holder_contact,
          holder_email,
          holder_phone
        )
      `);

    if (filters.type) {
      query = query.eq('application_type', filters.type);
    }
    
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    
    if (filters.lot_id) {
      query = query.eq('lot_id', filters.lot_id);
    }
    
    if (filters.applicant_id) {
      query = query.eq('applicant_id', filters.applicant_id);
    }
    
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }
};